"use client";

import { cn } from "@frontend/shared/lib/utils";
import Image from "next/image";
import type { LogoProps } from "../../app/directories/shop/[clientSlug]/types";
import Link from "next/link";
import { cva } from "class-variance-authority";
import type { NavbarBgColorType } from "./Navbar.types";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { SearchIcon, X, Menu } from "lucide-react";
import { NavbarSearchResults } from "./NavbarSearchResults";
import type { GetProductsByCategoryIdQuery } from "@frontend/lib/graphql/generated-gql";
// import { AccountModalWrapper } from "../account-modal/AccountModal";
import { usePathname } from "next/navigation";
import LanguageSelector from "@/app/directories/account/[userId]/(main)/clientComponents/LanguageSelector";
import { useTranslations } from "@/lib/next-intl-typed";
import type { LanguageCode } from "@frontend/lib/graphql/generated-gql";
import { useLocale } from "next-intl";
import { InsuranceStatusButton } from "./InsuranceStatusButton";

const MotionSearch = motion(SearchIcon);

const navBarVariants = cva("w-full fixed top-0 left-0 z-[51]", {
  variants: {
    bgMode: {
      white: "bg-white border-b-[#00000033] border-b",
      black: "bg-black",
      transparent: "bg-transparent",
      custom: "",
    },
    isSearch: {
      true: "backdrop-filter backdrop-blur-xl  bg-opacity-60 h-screen items-start",
      false: "",
    },
    isBoutique: {
      true: "bg-white border-b-0",
      false: "",
    },
  },
  defaultVariants: {
    bgMode: "white",
  },
});

const getTextColor = (bgMode: NavbarBgColorType) => {
  switch (bgMode) {
    case "black":
    case "transparent":
    case "custom":
      return "text-white";
    case "white":
      return "text-black";
    default:
      return "text-black";
  }
};

const getPlaceholderColor = (bgMode: NavbarBgColorType) => {
  switch (bgMode) {
    case "black":
    case "transparent":
    case "custom":
      return "placeholder:text-white";
    case "white":
      return "placeholder:text-gray-500";
    default:
      return "placeholder:text-gray-500";
  }
};

export const Navbar = ({
  params,
  classNames,
  logo,
  bgMode = "white",
  products,
  productPageBg,
  productPageBgMode = "white",
  logoRedirectUrl,
  homePageBg,
  isBoutique,
  orgSupportedLanguages,
  showLanguageSelector,
}: {
  params: { clientSlug: string };
  classNames: string;
  logo: LogoProps;
  bgMode?: NavbarBgColorType;
  productPageBgMode?: NavbarBgColorType;
  productPageBg?: string;
  homePageBg?: string;
  logoRedirectUrl?: string;
  products: GetProductsByCategoryIdQuery["findManyProducts"];
  isBoutique: boolean;
  orgSupportedLanguages: LanguageCode[];
  showLanguageSelector?: boolean;
}) => {
  const clientSlug = params.clientSlug;
  const [isSearch, setIsSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const inputRef = useRef<SVGSVGElement | null>(null);
  const scrollYRef = useRef(0);
  const pathname = usePathname();
  const isNotMainPage =
    pathname.includes("product") ||
    pathname.includes("schedule") ||
    pathname.includes("review");

  const isTransparent = bgMode === "transparent" || bgMode === "custom";

  useEffect(() => {
    if (!isTransparent) return;
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isTransparent]);

  useEffect(() => {
    if (isSearch) {
      scrollYRef.current = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollYRef.current);
    }

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      setSearchQuery("");
    };
  }, [isSearch]);

  const pageSpecificBgMode = isNotMainPage ? productPageBgMode : bgMode;

  // Hide the insurance status button on pages where the user is finalizing
  // their purchase — the review/complete screens
  const isInsuranceStatusBlacklistedRoute =
    pathname.includes("/review") ||
    pathname.includes("/complete") ||
    pathname.includes("/schedule");
  const showInsuranceStatusButton =
    !isInsuranceStatusBlacklistedRoute &&
    products.some((product) =>
      product.productToHrt.some((pth) =>
        pth.hrt.hrtPaymentType.some((pt) => pt.paymentType === "INSURED"),
      ),
    );
  const productPageLogoUrl = logo.url_product_page ?? logo.url;
  const locale = useLocale();
  const languageCode = locale.replace("-", "_");
  const t = useTranslations("Shop");

  return (
    <>
      <div
        style={{
          ...(productPageBg &&
            isNotMainPage && { backgroundColor: productPageBg }),
          ...(homePageBg &&
            !isNotMainPage &&
            bgMode === "custom" && { backgroundColor: homePageBg }),
        }}
        className={cn(
          navBarVariants({ bgMode: pageSpecificBgMode, isSearch, isBoutique }),
          isTransparent &&
            isScrolled &&
            !isSearch &&
            "backdrop-blur-md bg-black/80 transition-colors duration-500 border-b border-black/10",
          classNames,
        )}
      >
        <div
          className={cn(
            "lg:px-24 sm:px-5 lg:h-20 sm:h-14 sm:w-full flex justify-between items-center w-full",
            isBoutique && "lg:h-20 sm:h-14 lg:px-10",
          )}
        >
          {isSearch ? (
            <div className="flex flex-row w-full gap-4">
              <MotionSearch
                ref={inputRef}
                color={pageSpecificBgMode === "white" ? "#000000" : "#FFFFFF"}
                strokeWidth={1}
                onClick={() => setIsSearch(true)}
              />
              <input
                placeholder={t("Search")}
                className={cn(
                  "w-full font-focal bg-transparent h-[24px] outline-none text-base",
                  getTextColor(pageSpecificBgMode),
                  getPlaceholderColor(pageSpecificBgMode),
                )}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                value={searchQuery}
              />
            </div>
          ) : (
            <Link
              href={
                !isNotMainPage && logoRedirectUrl
                  ? logoRedirectUrl
                  : `/${clientSlug}`
              }
              className="text-white z-50"
            >
              {logo.url && (
                <Image
                  src={
                    isNotMainPage ? (productPageLogoUrl as string) : logo.url
                  }
                  alt={logo.alt || "Logo"}
                  width={160} // Set to 0 to allow full width
                  height={40}
                  className={cn(
                    "h-5 lg:h-10 w-auto object-contain",
                    isBoutique && "lg:h-8",
                  )} // Using Tailwind's standard classes
                  quality={100}
                  unoptimized={true}
                />
              )}
            </Link>
          )}

          <div className="hidden md:flex gap-16 items-center w-full justify-end z-50">
            <div className="flex">
              {isSearch ? (
                <X
                  className="cursor-pointer mix-blend-difference z-50"
                  color={isBoutique ? "black" : "white"}
                  strokeWidth={1}
                  onClick={() => {
                    setIsSearch(false);
                  }}
                />
              ) : (
                <MotionSearch
                  strokeWidth={1}
                  className="cursor-pointer mix-blend-difference z-50"
                  color={isBoutique ? "black" : "white"}
                  onClick={() => {
                    inputRef.current?.focus();
                    setIsSearch(true);
                  }}
                />
              )}
            </div>

            {showLanguageSelector && (
              <LanguageSelector
                className="w-fit bg-transparent border-none text-white md:flex hidden"
                contentClassName="rounded-2xl !z-[9999]"
                primaryLanguage={
                  languageCode
                    ? (languageCode as LanguageCode)
                    : ("en" as LanguageCode)
                }
                orgSupportedLanguages={orgSupportedLanguages}
              />
            )}

            {!isSearch && showInsuranceStatusButton && (
              <InsuranceStatusButton />
            )}

            {/* <AccountModalWrapper bgMode={bgMode} /> */}
          </div>
          <div className="md:hidden flex gap-4 items-center w-full justify-end z-50">
            {!isSearch && showInsuranceStatusButton && (
              <InsuranceStatusButton />
            )}
            {isSearch ? (
              <X
                className="cursor-pointer mix-blend-difference z-50"
                color={isBoutique ? "black" : "white"}
                strokeWidth={1}
                onClick={() => {
                  setIsSearch(false);
                }}
              />
            ) : (
              <Menu
                className="cursor-pointer mix-blend-difference z-50"
                color={isBoutique ? "black" : "white"}
                strokeWidth={1}
                onClick={() => {
                  setIsSearch(true);
                }}
              />
            )}
          </div>
        </div>

        {isSearch && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "calc(100vh - 6rem)" }}
            exit={{
              height: 0,
              opacity: 0,
              transition: {
                height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                opacity: { duration: 0.2 },
              },
            }}
            className={cn(
              "flex flex-col w-full fixed top-24 left-0 lg:px-24 sm:px-4 overflow-hidden justify-between",
            )}
          >
            {searchQuery.length > 0 ? (
              <NavbarSearchResults
                clientSlug={clientSlug}
                searchQuery={searchQuery}
                products={products}
                setIsSearch={setIsSearch}
                bgMode={pageSpecificBgMode}
                isBoutique={isBoutique}
              />
            ) : (
              <div className="flex flex-col w-full h-full gap-2" />
            )}
            <div className="pb-4 flex md:hidden flex-col gap-4">
              {/* <div className="w-fit">
              <AccountModalWrapper
                bgMode={bgMode}
                isSticky={true}
                direction="up"
                setMobileMenuVisible={setIsSearch}
              />
            </div> */}
              {showLanguageSelector && (
                <LanguageSelector
                  className="w-fit bg-transparent border-none text-white"
                  contentClassName="rounded-2xl !z-[9999]"
                  primaryLanguage={
                    languageCode
                      ? (languageCode as LanguageCode)
                      : ("en" as LanguageCode)
                  }
                  orgSupportedLanguages={orgSupportedLanguages}
                />
              )}
            </div>
          </motion.div>
        )}
      </div>
    </>
  );
};
